// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { DealerModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import DistanceDisplay, { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import GoogleMapsDirectionLink from "@insite/content-library/Components/GoogleMapsDirectionLink";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import MapPin from "@insite/mobius/Icons/MapPin";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ContactTypography from "@keller/App/Components/Common/ContactTypography";
import { styledBoxStyle } from "@keller/App/Components/Common/StyledBox";
import ContactUsFormModal, { ContactUsLocationModalState } from "@keller/App/Components/ContactUs/ContactUsFormModal";
import ShowroomModal from "@keller/App/Components/Locations/ShowroomModal";
import Notify from "@keller/App/Components/Notify";
import Chat from "@keller/XDL/Icons/Chat";
import Showroom from "@keller/XDL/Icons/Showroom";
import { color, mergeWithCss, parseBoolean, xMask, xScreen } from "@keller/XDL/Utilities";
import { iconLinkCommonStyles } from "@keller/XDL/Utilities/links";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    dealers?: DealerModel[] | null;
    defaultDistanceUnitOfMeasure?: string;
    locationDetailsPageLink?: string;
}

export interface DealerLinesStyles {
    wrapper?: InjectableCss;
    content?: InjectableCss;
    nameLink?: LinkPresentationProps;
    phoneLink?: TypographyPresentationProps;
    locationPhoneNumberLink?: LinkPresentationProps;
    locationAddressInfoCondensed?: AddressInfoCondensedDisplayStyles;
    locationDistanceDisplayText?: TypographyPresentationProps;
    actionsGridContainer?: GridContainerProps;
    contactUsGridItem?: GridItemProps;
    googleMapsDirectionLinkGridItem?: GridItemProps;
    locationGoogleMapsDirectionLink?: LinkPresentationProps;
    showroomInfoGridItem?: GridItemProps;
    showroomInfoLink?: LinkPresentationProps;
}

export const DealerLinesStyles: DealerLinesStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 25px;

            @media print {
                page-break-after: always;
            }
        `,
    },
    content: {
        css: css`
            width: 100%;
            max-width: 298px;
            ${xScreen("lte", "xs")} {
                max-width: initial;
            }

            ${styledBoxStyle}
        `,
    },
    nameLink: {
        color: "primary",
        typographyProps: {
            weight: "bold",
            ellipsis: true,
        },
        css: css`
            margin-bottom: 10px;
            width: 100%;
        `,
    },
    phoneLink: {
        color: "text.link",
        css: css`
            padding-top: 10px;
            font-size: 12px;
            font-weight: bold;
            color: ${color("text.link")};
        `,
    },
    locationDistanceDisplayText: {
        size: 12,
        lineHeight: "18px",
        css: css`
            display: block;
            margin: 5px 0 10px;
        `,
    },
    locationAddressInfoCondensed: {
        addressText: {
            css: css`
                font-size: 12px;
            `,
        },
        cityStatePostalCodeText: {
            css: css`
                font-size: 12px;
            `,
        },
        countryText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    actionsGridContainer: {
        gap: 10,
    },
    contactUsGridItem: {
        width: [12, 4, 4, 4, 4],
    },
    googleMapsDirectionLinkGridItem: {
        width: [12, 4, 4, 4, 4],
    },
    locationGoogleMapsDirectionLink: {
        ...iconLinkCommonStyles(MapPin),
    },
    showroomInfoGridItem: {
        width: [12, 4, 4, 4, 4],
    },
    showroomInfoLink: {
        ...mergeWithCss(iconLinkCommonStyles(Showroom), {
            icon: {
                iconProps: {
                    width: 18,
                },
            },
        }),
        css: css`
            justify-content: center;
        `,
    },
};

const styles = DealerLinesStyles;

const DealerLines: FC<OwnProps> = ({ dealers, defaultDistanceUnitOfMeasure, locationDetailsPageLink }) => {
    const [contactUsModal, setContactUsModal] = useState<ContactUsLocationModalState>({
        location: undefined,
    });
    const [showroomModal, setShowroomModal] = React.useState<{
        isOpen?: boolean;
        dealer?: DealerModel;
    }>({});

    const contactUsLinkClickHandler = (location?: LocationModel | DealerModel) => {
        setContactUsModal({
            location,
        });
    };

    const modalCloseHandler = () => {
        setContactUsModal({
            location: undefined,
        });
    };

    const showroomInfoHandler = (dealer: DealerModel) => {
        setShowroomModal({
            isOpen: true,
            dealer,
        });
    };

    const showroomModalCloseHandler = () => {
        setShowroomModal({
            isOpen: false,
            dealer: undefined,
        });
    };

    if (!dealers || dealers.length === 0) {
        return (
            <StyledWrapper>
                <Notify type="info" data-test-selector="locationSearchForm_siteErrorMessage">
                    {siteMessage("DealerLocator_NoResultsMessage")}
                </Notify>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {dealers.map(dealer => {
                return (
                    <StyledWrapper key={dealer.id} {...styles.content}>
                        {locationDetailsPageLink && (
                            <Link
                                href={`${locationDetailsPageLink}/DealerDetails?id=${dealer.id}`}
                                {...styles.nameLink}
                            >
                                {dealer.name}
                            </Link>
                        )}
                        <AddressInfoCondensedDisplay extendedStyles={styles.locationAddressInfoCondensed} {...dealer} />
                        {dealer.phone && (
                            <ContactTypography
                                type="phone"
                                targetValue={dealer.phone}
                                icon={true}
                                {...styles.phoneLink}
                            >
                                {xMask.phoneNumber(dealer.phone)}
                            </ContactTypography>
                        )}
                        <DistanceDisplay
                            {...styles.locationDistanceDisplayText}
                            distance={dealer.distance}
                            unitOfMeasure={
                                (dealer.distanceUnitOfMeasure || defaultDistanceUnitOfMeasure) as DistanceUnitOfMeasure
                            }
                        />
                        <GridContainer {...styles.actionsGridContainer}>
                            <GridItem {...styles.contactUsGridItem}>
                                <Link
                                    onClick={() => contactUsLinkClickHandler(dealer)}
                                    data-test-selector="contactUsLinkClick"
                                    {...iconLinkCommonStyles(Chat)}
                                >
                                    {translate("Contact Us")}
                                </Link>
                            </GridItem>
                            <GridItem {...styles.googleMapsDirectionLinkGridItem}>
                                <GoogleMapsDirectionLink {...styles.locationGoogleMapsDirectionLink} {...dealer} />
                            </GridItem>
                            {parseBoolean(dealer.properties.isShowroomLocation) && (
                                <GridItem {...styles.showroomInfoGridItem}>
                                    <Link {...styles.showroomInfoLink} onClick={() => showroomInfoHandler(dealer)}>
                                        {translate("Showroom")}
                                    </Link>
                                </GridItem>
                            )}
                        </GridContainer>
                    </StyledWrapper>
                );
            })}
            <ContactUsFormModal location={contactUsModal.location} handleClose={modalCloseHandler} />
            <ShowroomModal
                isOpen={showroomModal.isOpen}
                dealer={showroomModal.dealer}
                modalCloseHandler={showroomModalCloseHandler}
            />
        </StyledWrapper>
    );
};

export default DealerLines;
